import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RichTextEditor from '../components/RichTextEditor';
import PreviewNoticia from '../components/PreviewNoticia';
import VisualEditor from '../components/VisualEditor';
import { useAdminAuth } from '../context/AdminAuthContext';
import { LogOut, Send, CheckCircle, AlertCircle, FileText, Tag, Globe, Image, Video, Eye, Plus, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { db } from '../firebase/config';
import { collection, getDocs, deleteDoc, doc, orderBy, query } from 'firebase/firestore';


const FORM_VAZIO = {
  titulo: '',
  subtitulo: '',
  conteudo_html: '',
  tags: '',
  fonte: '',
  imagem_capa: '',
  video_url: '',
  categoria: 'Direito',
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { autenticado, logout } = useAdminAuth();

  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState('');
  const [preview, setPreview] = useState(false);

  const [view, setView] = useState('list');
  const [noticiasList, setNoticiasList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingNoticia, setEditingNoticia] = useState(null);

  useEffect(() => {
    if (!autenticado) {
      navigate('/adm');
    }
  }, [autenticado, navigate]);

  const fetchNoticias = async () => {
    try {
      const q = query(collection(db, 'noticias'), orderBy('data', 'desc'));
      const snap = await getDocs(q);
      setNoticiasList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      setNoticiasList([]);
    }
  };

  useEffect(() => {
    if (view === 'list') {
      fetchNoticias();
    }
  }, [view]);

  const handleDeleteNoticia = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta notícia?')) return;
    try {
      await deleteDoc(doc(db, 'noticias', id));
      setNoticiasList((prev) => prev.filter((n) => n.id !== id));
    } catch {
      alert('Erro ao excluir notícia.');
    }
  };

  const handleEditNoticia = (noticia) => {
    setEditingNoticia(noticia);
    setEditingId(noticia.id);
    setView('edit');
  };

  const handleChange = (campo) => (e) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  const handleQuill = (valor) => {
    setForm((prev) => ({ ...prev, conteudo_html: valor }));
  };

  const handleLogout = () => {
    logout();
    navigate('/adm');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setSucesso(false);

    if (!form.titulo.trim()) {
      setErro('O título é obrigatório.');
      return;
    }
    if (!form.conteudo_html || form.conteudo_html === '<p><br></p>') {
      setErro('O texto da notícia é obrigatório.');
      return;
    }

    // O painel é um rascunho: a publicação final acontece no Preview
    setPreview(true);
  };

  if (!autenticado) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Barra superior */}
      <header className="bg-[#0F172A] text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <FileText size={22} className="text-[#B49163]" />
          <div>
            <h1 className="font-bold text-lg leading-none">Painel CMS</h1>
            <p className="text-gray-400 text-xs">Universo do Direito</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {view === 'list' && (
            <button
              onClick={() => { setForm(FORM_VAZIO); setEditingId(null); setErro(''); setSucesso(false); setView('create'); }}
              className="flex items-center gap-2 text-sm bg-[#B49163] hover:bg-[#9a7a52] text-white font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              <Plus size={16} />
              Nova Publicação
            </button>
          )}
          <a
            href="https://www.universododireito.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            <ExternalLink size={16} />
            Ver site
          </a>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            <LogOut size={16} />
            Sair
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10">

        {/* ── VIEW: LISTA ── */}
        {view === 'list' && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-serif font-bold text-[#0F172A]">Publicações</h2>
              <span className="text-sm text-gray-400">{noticiasList.length} notícia(s)</span>
            </div>

            {noticiasList.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <FileText size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg">Nenhuma publicação encontrada.</p>
                <p className="text-sm mt-1">Clique em "Nova Publicação" para começar.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {noticiasList.map((noticia) => {
                  const dataFormatada = noticia.data?.toDate
                    ? noticia.data.toDate().toLocaleDateString('pt-BR')
                    : noticia.data
                    ? new Date(noticia.data).toLocaleDateString('pt-BR')
                    : '';
                  return (
                    <div key={noticia.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                      {noticia.imagem_capa ? (
                        <img
                          src={noticia.imagem_capa}
                          alt={noticia.titulo}
                          className="w-full h-40 object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
                          <Image size={32} className="text-gray-300" />
                        </div>
                      )}
                      <div className="p-4 flex flex-col flex-1">
                        <span className="text-xs font-semibold text-[#B49163] uppercase tracking-wide mb-1">
                          {noticia.categoria || 'Direito'}
                        </span>
                        <h3 className="font-bold text-[#0F172A] text-sm leading-snug line-clamp-2 flex-1">
                          {noticia.titulo}
                        </h3>
                        {dataFormatada && (
                          <p className="text-xs text-gray-400 mt-2">{dataFormatada}</p>
                        )}
                        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                          <button
                            onClick={() => handleEditNoticia(noticia)}
                            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-[#0F172A] border border-gray-300 hover:border-[#0F172A] hover:bg-gray-50 py-2 rounded-lg transition-colors"
                          >
                            <Pencil size={13} /> Editar
                          </button>
                          <button
                            onClick={() => handleDeleteNoticia(noticia.id)}
                            className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-red-600 border border-red-200 hover:border-red-400 hover:bg-red-50 py-2 rounded-lg transition-colors"
                          >
                            <Trash2 size={13} /> Excluir
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── VIEW: CRIAR ── */}
        {view === 'create' && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-serif font-bold text-[#0F172A]">
                Nova Publicação
              </h2>
              <button
                type="button"
                onClick={() => { setView('list'); setEditingId(null); setForm(FORM_VAZIO); setErro(''); setSucesso(false); }}
                className="text-sm text-gray-500 hover:text-[#0F172A] border border-gray-300 hover:border-gray-400 px-4 py-2 rounded-lg transition-colors"
              >
                ← Cancelar
              </button>
            </div>

            {/* Alerta de sucesso */}
            {sucesso && (
              <div className="flex items-center gap-3 bg-green-50 border border-green-300 text-green-800 rounded-xl px-5 py-4 mb-6">
                <CheckCircle size={20} className="flex-shrink-0" />
                <span className="font-semibold">Notícia publicada com sucesso!</span>
              </div>
            )}

            {/* Alerta de erro */}
            {erro && (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-5 py-4 mb-6">
                <AlertCircle size={20} className="flex-shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Título */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  <FileText size={15} /> Título *
                </label>
                <input
                  type="text"
                  value={form.titulo}
                  onChange={handleChange('titulo')}
                  placeholder="Digite o título da notícia..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#B49163] focus:border-transparent"
                />
              </div>

              {/* Subtítulo (opcional) */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  <FileText size={15} /> Subtítulo
                </label>
                <input
                  type="text"
                  value={form.subtitulo}
                  onChange={handleChange('subtitulo')}
                  placeholder="Linha de apoio ao título (opcional)..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-[#B49163] focus:border-transparent"
                />
              </div>

              {/* Editor de Texto */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-3">
                  <FileText size={15} /> Texto da Notícia *
                </label>
                <RichTextEditor
                  value={form.conteudo_html}
                  onChange={handleQuill}
                  placeholder="Escreva o conteúdo completo da notícia aqui..."
                />
              </div>

              {/* Tags e Categoria */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                    <Tag size={15} /> Tags (separadas por vírgula)
                  </label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={handleChange('tags')}
                    placeholder="ex: STF, Direito Penal, Habeas Corpus"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B49163] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                    <Tag size={15} /> Categoria
                  </label>
                  <select
                    value={form.categoria}
                    onChange={handleChange('categoria')}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B49163] focus:border-transparent bg-white"
                  >
                    <option>Direito</option>
                    <option>Direito Constitucional</option>
                    <option>Direito Penal</option>
                    <option>Direito Civil</option>
                    <option>Direito Trabalhista</option>
                    <option>Direito Tributário</option>
                    <option>Direito Previdenciário</option>
                    <option>Direito do Consumidor</option>
                    <option>Direito Administrativo</option>
                    <option>Direito Digital</option>
                    <option>Direito Internacional</option>
                  </select>
                </div>
              </div>

              {/* Fonte e Imagem de Capa */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                    <Globe size={15} /> Fonte
                  </label>
                  <input
                    type="text"
                    value={form.fonte}
                    onChange={handleChange('fonte')}
                    placeholder="ex: Conjur, STF, Redação"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B49163] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                    <Image size={15} /> URL da Imagem de Capa
                  </label>
                  <input
                    type="url"
                    value={form.imagem_capa}
                    onChange={handleChange('imagem_capa')}
                    placeholder="https://..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B49163] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Pré-visualização da imagem */}
              {form.imagem_capa && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Preview da imagem:</p>
                  <img
                    src={form.imagem_capa}
                    alt="Preview"
                    className="max-h-48 rounded-lg object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              {/* Vídeo */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                  <Video size={15} /> URL do Vídeo (YouTube / Vimeo) — opcional
                </label>
                <input
                  type="url"
                  value={form.video_url}
                  onChange={handleChange('video_url')}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#B49163] focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1">Cole o link normal do YouTube ou Vimeo. O embed será gerado automaticamente.</p>
              </div>

              {/* Botões: Preview + Publicar (abre preview para publicação) */}
              <div className="flex items-center justify-end gap-3">
                {view === 'create' && (
                  <button
                    type="button"
                    onClick={() => setPreview(true)}
                    className="inline-flex items-center gap-2 bg-white border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white font-bold px-7 py-3 rounded-xl transition-colors duration-200 text-base shadow-sm"
                  >
                    <Eye size={18} />
                    Pré-visualizar
                  </button>
                )}
                <button
                  type="submit"
                  disabled={salvando}
                  className="inline-flex items-center gap-2 bg-[#B49163] hover:bg-[#0F172A] text-white font-bold px-8 py-3 rounded-xl transition-colors duration-200 disabled:opacity-60 text-base shadow-lg"
                >
                  <Send size={18} />
                  {salvando ? 'Abrindo preview...' : 'Avançar para publicação'}
                </button>
              </div>
            </form>
          </>
        )}
      </main>

      {/* Editor Visual (view === 'edit') */}
      {view === 'edit' && editingNoticia && (
        <VisualEditor
          noticia={editingNoticia}
          onCancelar={() => { setView('list'); setEditingNoticia(null); setEditingId(null); }}
          onSalvo={() => { setView('list'); setEditingNoticia(null); setEditingId(null); }}
        />
      )}

      {/* Modal de Preview */}
      {preview && (
        <PreviewNoticia
          form={form}
          onCancelar={() => setPreview(false)}
          onPublishSuccess={() => {
            setPreview(false);
            setForm(FORM_VAZIO);
            setSucesso(true);
            setView('list');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
